import React, { useCallback, useEffect, useState } from 'react';

import Button from '../../components/Button/Button';
import alert from '../../helpers/alert';
import extractErrorMessage from '../../utils/extractErrorMessage';
import styles from './sheetSync.module.css';
import ProblemsTable from './ProblemsTable';
import { getSheetSyncState, runSheetSync, saveSheetLinks } from '../../api/prices';
import { KIND_LABELS, deltaText, formatDateTime, isBigJump, money } from './sheetSyncFormat';

const kindClass = (kind) =>
  [styles.kind, { created: styles.kindCreated, changed: styles.kindChanged, held: styles.kindHeld }[kind]]
    .filter(Boolean)
    .join(' ');

// Таблица «было → стало → разница»: общий вид для изменений и для ждущих подтверждения
const DiffTable = ({ items, selectable, selected, onToggle }) => (
  <table className={styles.table}>
    <thead>
      <tr>
        {selectable && <th />}
        <th>Что</th>
        <th>Марка</th>
        <th className={styles.num}>Было</th>
        <th className={styles.num}>Стало</th>
        <th className={styles.num}>Разница</th>
        <th>Примечание</th>
      </tr>
    </thead>
    <tbody>
      {items.map((i) => (
        <tr key={`${i.key}-${i.ruleId ?? 'new'}-${i.label}`}>
          {selectable && (
            <td>
              <input
                type="checkbox"
                aria-label={`Подтвердить ${i.key}`}
                checked={selected.has(i.key)}
                onChange={() => onToggle(i.key)}
              />
            </td>
          )}
          <td>
            <span className={kindClass(i.kind)}>{KIND_LABELS[i.kind]}</span> <b>{i.key}</b> {i.label}
          </td>
          <td>{i.mark}</td>
          <td className={`${styles.num} ${styles.was}`}>{money(i.oldPrice)}</td>
          <td className={`${styles.num} ${styles.now}`}>{money(i.newPrice)}</td>
          <td className={`${styles.num} ${isBigJump(i.oldPrice, i.newPrice) ? styles.deltaBig : ''}`}>
            {deltaText(i.oldPrice, i.newPrice)}
          </td>
          <td className={styles.muted}>{i.note}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Вкладка «Таблица»: что сейчас с Google-таблицей приказов — к применению, ждёт подтверждения, проблемы
const SheetSync = ({ marks = [], onProblemsCount }) => {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  // Применение меняет цены стока: одного клика мало, второй подтверждает (как на экране прайса)
  const [confirmKind, setConfirmKind] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    getSheetSyncState()
      .then((res) => {
        setState(res.data);
        setSelected(new Set());
        setConfirmKind(null);
        onProblemsCount?.(res.data?.view?.problems?.length || 0);
      })
      .catch((err) => {
        setState(null);
        setError(extractErrorMessage(err) || 'Не удалось прочитать таблицу');
      })
      .finally(() => setLoading(false));
  }, [onProblemsCount]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!confirmKind) return undefined;
    const timer = setTimeout(() => setConfirmKind(null), 6000);
    return () => clearTimeout(timer);
  }, [confirmKind]);

  const view = state?.view;
  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const run = async (kind, confirm = []) => {
    if (confirmKind !== kind) {
      setConfirmKind(kind);
      return;
    }
    setConfirmKind(null);
    setBusy(true);
    try {
      const res = await runSheetSync({ dryRun: false, confirm });
      const r = res.data.run;
      if (r.status === 'error') alert.error(r.message || 'Применение не удалось');
      else if (r.status === 'blocked') alert.error(r.message || 'Таблица остановлена проверками');
      else alert.success(`Применено: новых правил ${r.created}, изменено ${r.changed}. Запуск записан во вкладке «История».`);
      load();
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось применить таблицу');
    } finally {
      setBusy(false);
    }
  };

  const saveLinks = async (links, okText) => {
    setBusy(true);
    try {
      const res = await saveSheetLinks(links);
      if (res.data.errors?.length) {
        alert.error(res.data.errors.join('; '));
        return;
      }
      alert.success(okText);
      load();
    } catch (err) {
      alert.error(extractErrorMessage(err) || 'Не удалось сохранить привязку');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !state) return <div className={styles.empty}>Читаю таблицу…</div>;
  if (error)
    return (
      <>
        <div className={`${styles.banner} ${styles.bannerBad}`}>{error}</div>
        <Button text="Повторить" onClick={load} />
      </>
    );
  if (!view) return null;

  const problems = view.problems || [];
  const held = view.held || [];
  const changes = view.changes || [];

  return (
    <>
      <div className={styles.top}>
        {/* Button не пробрасывает title, поэтому подсказка — на обёртке */}
        <span title="Заново прочитать Google-таблицу и пересчитать экран. Ничего не записывает.">
          <Button text="Проверить таблицу" onClick={load} disabled={busy || loading} />
        </span>
        <span title="Записать правила из раздела «К применению» и привести цены машин в приложении к приказам. Нажимается дважды.">
          <Button
            text={confirmKind === 'apply' ? `Применить ${changes.length}? Это изменит цены стока` : `Применить изменения: ${changes.length}`}
            onClick={() => run('apply')}
            disabled={busy || loading || changes.length === 0 || !!view.blocked}
            fill
            style={confirmKind === 'apply' ? { borderColor: '#d84a4a', color: '#d84a4a' } : {}}
          />
        </span>
        <span className={`${styles.grow} ${styles.muted}`}>
          {state.lastRun ? `Последний запуск: ${formatDateTime(state.lastRun.startedAt)}` : 'Запусков ещё не было'}
          {state.enabled ? ' · автопроверка включена' : ' · автопроверка выключена: цены меняются только кнопкой'}
        </span>
      </div>

      <details className={styles.help}>
        <summary>Что делают кнопки на этом экране</summary>
        <ul>
          <li>
            <b>Проверить таблицу</b> — заново читает Google-таблицу и пересчитывает экран. Ничего не записывает.
          </li>
          <li>
            <b>Привязать</b> (у проблемной строки) — сохраняет привязку сразу, второй кнопки «Сохранить» нет. Привязка только
            говорит системе, к какому правилу приказа относится строка. Цены она не меняет: если цена в таблице отличается от цены
            правила, разница появится в «К применению».
          </li>
          <li>
            <b>Выбрать вручную</b> — указать модель, комплектацию, год, объём и привод самому, если подсказки не подошли.
          </li>
          <li>
            <b>Не синхронизировать</b> — исключить строку из синхронизации. Вернуть её можно на вкладке «Привязки».
          </li>
          <li>
            <b>Применить изменения</b> — единственная кнопка, которая меняет цены: записывает правила из «К применению» и
            приводит цены машин в приложении к приказам. Нажимается дважды, откат — вкладка «История».
          </li>
          <li>Все уже привязанные строки можно проверить, изменить или отвязать на вкладке «Привязки».</li>
        </ul>
      </details>

      {view.blocked && <div className={`${styles.banner} ${styles.bannerBad}`}>Таблица остановлена: {view.blocked}</div>}

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span>строк в таблице</span>
          <b>{view.rows}</b>
        </div>
        <div className={styles.stat}>
          <span>совпало с приказом</span>
          <b className={styles.ok}>{view.same}</b>
        </div>
        <div className={styles.stat}>
          <span>к применению</span>
          <b>{changes.length}</b>
        </div>
        <div className={styles.stat}>
          <span>ждут подтверждения</span>
          <b className={held.length ? styles.warn : ''}>{held.length}</b>
        </div>
        <div className={styles.stat}>
          <span>проблемы</span>
          <b className={problems.length ? styles.bad : ''}>{problems.length}</b>
        </div>
        <div className={styles.stat}>
          <span>не синхронизируются</span>
          <b>{view.ignored}</b>
        </div>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Проблемы <span className={styles.count}>{problems.length}</span>
        </h3>
        <div className={styles.hint}>
          Эти строки Google-таблицы система не смогла сама сопоставить с нашими данными (моделью и комплектацией в приказе). Для каждой выберите,
          какой позиции приказа она соответствует, и нажмите «Привязать», либо исключите строку. Привязка сохраняется сразу и цены не меняет: если цена в
          таблице другая, разница появится в «К применению». Пока строка не сопоставлена, её цена в прайс не попадает.
        </div>
        <ProblemsTable
          problems={problems}
          marks={marks}
          busy={busy}
          onLink={(links, okText) => saveLinks(links, okText)}
          onIgnore={(key) =>
            saveLinks([{ sheetKey: key, ignore: true }], `${key} исключена из синхронизации. Вернуть её можно на вкладке «Привязки».`)
          }
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          Ждут подтверждения <span className={styles.count}>{held.length}</span>
        </h3>
        <div className={styles.hint}>
          Скачок цены больше 10 % или первая цена у модели без правил. Отметьте то, что верно, и подтвердите: это применит
          выбранное вместе с остальными изменениями.
        </div>
        {held.length === 0 ? (
          <div className={styles.empty}>Ничего не ждёт</div>
        ) : (
          <>
            <DiffTable items={held} selectable selected={selected} onToggle={toggle} />
            <div className={styles.rowActions}>
              <Button
                text={confirmKind === 'held' ? `Подтвердить ${selected.size} и применить?` : 'Подтвердить выбранные и применить'}
                onClick={() => run('held', [...selected])}
                disabled={busy || selected.size === 0}
                style={confirmKind === 'held' ? { borderColor: '#d84a4a', color: '#d84a4a' } : {}}
              />
            </div>
          </>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>
          К применению <span className={styles.count}>{changes.length}</span>
        </h3>
        <div className={styles.hint}>
          Изменения, прошедшие проверки. Кнопка «Применить изменения» запишет эти правила и приведёт цены стока к приказам.
        </div>
        {changes.length === 0 ? <div className={styles.empty}>Таблица совпадает с приказом</div> : <DiffTable items={changes} />}
      </section>
    </>
  );
};

export default SheetSync;
